import { useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatINR } from "@/lib/quotationApi";

// The "From master" / "Custom item" add forms for Step 3. Owns its own local
// form state; calls onAdd(item) with the built accessory (no id).
export default function AddAccessoryForms({ masterAccessories, onAdd }) {
  const [addMode, setAddMode] = useState(null); // 'master' | 'custom' | null
  const [selectedMasterId, setSelectedMasterId] = useState("");
  const [masterQty, setMasterQty] = useState("1");
  const [customName, setCustomName] = useState("");
  const [customQty, setCustomQty] = useState("1");
  const [customPrice, setCustomPrice] = useState("");

  const addFromMaster = () => {
    if (!selectedMasterId) return;
    const m = masterAccessories.find((x) => x.id === selectedMasterId);
    if (!m) return;
    onAdd({
      name: m.name,
      quantity: parseFloat(masterQty) || 1,
      price_per_unit: m.price_per_unit,
      custom: false,
    });
    setSelectedMasterId("");
    setMasterQty("1");
    setAddMode(null);
  };

  const addCustom = () => {
    if (!customName.trim()) return;
    onAdd({
      name: customName.trim(),
      quantity: parseFloat(customQty) || 1,
      price_per_unit: parseFloat(customPrice) || 0,
      custom: true,
    });
    setCustomName("");
    setCustomQty("1");
    setCustomPrice("");
    setAddMode(null);
  };

  if (addMode === null) {
    return (
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setAddMode("master")}
          className="btn-ghost flex items-center justify-center gap-2"
          data-testid="add-from-master-btn"
        >
          <Plus size={16} />
          From master
        </button>
        <button
          onClick={() => setAddMode("custom")}
          className="btn-ghost flex items-center justify-center gap-2"
          data-testid="add-custom-btn"
        >
          <Sparkles size={16} />
          Custom item
        </button>
      </div>
    );
  }

  if (addMode === "master") {
    return (
      <div className="card-luxe">
        <div className="overline text-[#C5A880] mb-4">Add from master</div>
        <label className="label-luxe">Accessory</label>
        <Select value={selectedMasterId} onValueChange={setSelectedMasterId}>
          <SelectTrigger className="input-luxe !text-white" data-testid="master-select">
            <SelectValue placeholder="Pick an accessory" />
          </SelectTrigger>
          <SelectContent className="bg-[#1D212C] border-white/10 text-white">
            {masterAccessories.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.name} · {formatINR(m.price_per_unit)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="label-luxe mt-4">Quantity</label>
        <input
          type="number"
          className="input-luxe"
          inputMode="numeric"
          value={masterQty}
          onChange={(e) => setMasterQty(e.target.value)}
          data-testid="master-qty-input"
        />
        <div className="flex gap-2 mt-4">
          <button className="btn-ghost flex-1" onClick={() => setAddMode(null)}>
            Cancel
          </button>
          <button
            className="btn-primary flex-1"
            onClick={addFromMaster}
            disabled={!selectedMasterId}
            data-testid="master-confirm-add"
          >
            Add
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-luxe">
      <div className="overline text-[#C5A880] mb-4">Custom accessory</div>
      <label className="label-luxe">Name</label>
      <input
        className="input-luxe"
        value={customName}
        onChange={(e) => setCustomName(e.target.value)}
        placeholder="e.g. Magic Corner"
        data-testid="custom-name-input"
      />
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className="label-luxe">Quantity</label>
          <input
            type="number"
            className="input-luxe"
            inputMode="numeric"
            value={customQty}
            onChange={(e) => setCustomQty(e.target.value)}
            data-testid="custom-qty-input"
          />
        </div>
        <div>
          <label className="label-luxe">Unit price (₹)</label>
          <input
            type="number"
            className="input-luxe"
            inputMode="numeric"
            value={customPrice}
            onChange={(e) => setCustomPrice(e.target.value)}
            data-testid="custom-price-input"
          />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <button className="btn-ghost flex-1" onClick={() => setAddMode(null)}>
          Cancel
        </button>
        <button
          className="btn-primary flex-1"
          onClick={addCustom}
          disabled={!customName.trim()}
          data-testid="custom-confirm-add"
        >
          Add
        </button>
      </div>
    </div>
  );
}
